import React, { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Box } from '@mui/material';
import { X } from 'lucide-react';
import Button from "@albertsons/uds/molecule/Button";

interface ErrorModalProps {
  open: boolean;
  onClose: () => void;
  errors: string[];
  title?: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  open,
  onClose,
  errors,
  title = "All Errors"
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);
  //const errorLinesCount = errors.filter(e => /^Error.*line /i.test(e) || /^Error/i.test(e)).length;
  const errorLinesCount = errors.filter(e => /^Error.*line /i.test(e) || /^Error in Shipment ID.*/i.test(e)).length;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {title} ({errorLinesCount} total)
        </Typography>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: '#666',
            '&:hover': {
              backgroundColor: '#f5f5f5',
              color: '#333'
            }
          }}
          aria-label="Close error modal"
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        flex: 1, 
        overflow: 'auto',
        px: 3,
        py: 2
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {errors.map((error, index) => {
            // Render blank lines (empty strings) without styling
            if (error === '') {
              return (
                <Box key={index} sx={{ height: '12px' }} />
              );
            }
            
            
            if (error.startsWith('🔴 MA System Errors:')) {
              return (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    backgroundColor: '#fff3e0',
                    border: '2px solid #ff9800',
                    borderRadius: '6px',
                    fontSize: '15px',
                    lineHeight: 1.5,
                    fontWeight: 'bold'
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                    {error}
                  </Typography>
                </Box>
              );
            }

            
            if (error.includes('.csv')) {
              return (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    backgroundColor: '#fff3e0',
                    border: '2px solid #ff9800',
                    borderRadius: '6px',
                    fontSize: '15px',
                    lineHeight: 1.5,
                    fontWeight: 'bold'
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                    {error}
                  </Typography>
                </Box>
              );
            }

            // Regular error styling
            return (
              <Box
                key={index}
                sx={{
                  p: 2,
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: 1.5
                }}
              >
                <Typography variant="body2" sx={{ color: '#dc2626' }}>
                  {error}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        borderTop: '1px solid #e0e0e0',
        justifyContent: 'flex-end'
      }}>
        <Button onClick={onClose} width={100}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorModal;
